'use client';

import * as Sentry from '@sentry/nextjs';
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer
} from 'react';
import { useRouter } from 'next/navigation';

import { api, setSessionExpiryHandlers } from '$lib/api/client';
import type {
	AuthResponse,
	ImpersonationResponse,
	InviteSignupRequest,
	UserResponse
} from '$lib/api/types';
import { getAuthenticatedEntryPath } from '$lib/auth/routing';

export interface ImpersonationInfo {
	targetCompany: { id: number; name: string };
	expiresAt: number;
}

type AuthState = {
	user: UserResponse | null;
	loading: boolean;
	impersonation: ImpersonationInfo | null;
};

type AuthAction =
	| { type: 'loading'; value: boolean }
	| { type: 'setUser'; user: UserResponse | null; impersonation?: ImpersonationInfo | null }
	| { type: 'setImpersonation'; impersonation: ImpersonationInfo | null };

type AuthContextValue = AuthState & {
	isLoggedIn: boolean;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	hasCompany: boolean;
	isImpersonating: boolean;
	setUser: (user: UserResponse | null) => void;
	init: () => Promise<void>;
	login: (email: string, password: string) => Promise<AuthResponse>;
	signup: (email: string, password: string, name: string) => Promise<AuthResponse>;
	signupWithInvite: (request: InviteSignupRequest) => Promise<AuthResponse>;
	logout: () => void;
	startImpersonation: (companyId: number) => Promise<void>;
	stopImpersonation: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function authReducer(state: AuthState, action: AuthAction): AuthState {
	switch (action.type) {
		case 'loading':
			return { ...state, loading: action.value };
		case 'setUser':
			return {
				...state,
				user: action.user,
				impersonation: action.impersonation === undefined ? state.impersonation : action.impersonation
			};
		case 'setImpersonation':
			return { ...state, impersonation: action.impersonation };
		default: {
			const exhaustive: never = action;
			return exhaustive;
		}
	}
}

function applySentryScope(user: UserResponse | null, impersonating: boolean) {
	if (!user) {
		Sentry.setUser(null);
		Sentry.getCurrentScope().clear();
		return;
	}
	Sentry.setUser({ id: String(user.id) });
	Sentry.setTag('role', user.role);
	if (user.companyId != null) Sentry.setTag('company.id', String(user.companyId));
	Sentry.setTag('impersonation', impersonating ? 'true' : 'false');
}

function readImpersonationInfo(): ImpersonationInfo | null {
	if (typeof window === 'undefined') return null;
	const token = localStorage.getItem('impersonationToken');
	const targetRaw = localStorage.getItem('impersonationTargetCompany');
	const expiresAtRaw = localStorage.getItem('impersonationExpiresAt');
	if (!token || !targetRaw || !expiresAtRaw) return null;
	try {
		return {
			targetCompany: JSON.parse(targetRaw) as ImpersonationInfo['targetCompany'],
			expiresAt: Number(expiresAtRaw)
		};
	} catch {
		return null;
	}
}

function clearScopedStorage() {
	api.clearImpersonation();
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const router = useRouter();
	const [state, dispatch] = useReducer(authReducer, {
		user: null,
		loading: true,
		impersonation: null
	});

	const setUser = useCallback((user: UserResponse | null) => {
		dispatch({ type: 'setUser', user });
		applySentryScope(user, readImpersonationInfo() !== null);
	}, []);

	const init = useCallback(async () => {
		dispatch({ type: 'loading', value: true });
		const impersonation = readImpersonationInfo();
		dispatch({ type: 'setImpersonation', impersonation });
		const token =
			typeof window !== 'undefined'
				? (localStorage.getItem('impersonationToken') ?? localStorage.getItem('accessToken'))
				: null;

		if (!token) {
			dispatch({ type: 'setUser', user: null, impersonation: null });
			dispatch({ type: 'loading', value: false });
			applySentryScope(null, false);
			return;
		}

		try {
			const me = await api.get<UserResponse>('/auth/me');
			dispatch({ type: 'setUser', user: me, impersonation });
			applySentryScope(me, impersonation !== null);
		} catch {
			clearScopedStorage();
			api.clearTokens();
			dispatch({ type: 'setUser', user: null, impersonation: null });
			applySentryScope(null, false);
		} finally {
			dispatch({ type: 'loading', value: false });
		}
	}, []);

	const login = useCallback(
		async (email: string, password: string) => {
			const response = await api.post<AuthResponse>('/auth/login', { email, password });
			clearScopedStorage();
			api.setTokens(response.accessToken, response.refreshToken);
			dispatch({ type: 'setUser', user: response.user, impersonation: null });
			applySentryScope(response.user, false);
			router.push(getAuthenticatedEntryPath(response.user));
			return response;
		},
		[router]
	);

	const signup = useCallback(
		async (email: string, password: string, name: string) => {
			const response = await api.post<AuthResponse>('/auth/signup', { email, password, name });
			clearScopedStorage();
			api.setTokens(response.accessToken, response.refreshToken);
			dispatch({ type: 'setUser', user: response.user, impersonation: null });
			applySentryScope(response.user, false);
			router.push(getAuthenticatedEntryPath(response.user));
			return response;
		},
		[router]
	);

	const signupWithInvite = useCallback(
		async (request: InviteSignupRequest) => {
			const response = await api.post<AuthResponse>('/auth/signup/invite', request);
			clearScopedStorage();
			api.setTokens(response.accessToken, response.refreshToken);
			dispatch({ type: 'setUser', user: response.user, impersonation: null });
			applySentryScope(response.user, false);
			router.push(getAuthenticatedEntryPath(response.user));
			return response;
		},
		[router]
	);

	const logout = useCallback(() => {
		api.clearTokens();
		clearScopedStorage();
		if (typeof window !== 'undefined') sessionStorage.setItem('antflow:logoutRedirect', '1');
		dispatch({ type: 'setUser', user: null, impersonation: null });
		applySentryScope(null, false);
		router.replace('/login');
	}, [router]);

	const startImpersonation = useCallback(
		async (companyId: number) => {
			const originalToken = localStorage.getItem('accessToken');
			if (!originalToken) throw new Error('SUPER_ADMIN 토큰이 없습니다.');
			const response = await api.post<ImpersonationResponse>(`/admin/impersonate/${companyId}`, {});
			localStorage.setItem('impersonationOriginalToken', originalToken);
			localStorage.setItem('impersonationToken', response.accessToken);
			localStorage.setItem('impersonationTargetCompany', JSON.stringify(response.targetCompany));
			localStorage.setItem('impersonationExpiresAt', String(new Date(response.expiresAt).getTime()));
			await init();
			router.push('/dashboard');
		},
		[init, router]
	);

	const stopImpersonation = useCallback(async () => {
		api.clearImpersonation();
		dispatch({ type: 'setImpersonation', impersonation: null });
		await init();
		router.push('/dashboard');
	}, [init, router]);

	useEffect(() => {
		setSessionExpiryHandlers({
			onUnauthorized: () => {
				dispatch({ type: 'setUser', user: null, impersonation: null });
				api.clearImpersonation();
				applySentryScope(null, false);
				router.replace('/login');
			},
			onImpersonationExpired: () => {
				dispatch({ type: 'setImpersonation', impersonation: null });
				void init().then(() => router.push('/dashboard'));
			}
		});
		void init();
		return () => setSessionExpiryHandlers(null);
	}, [init, router]);

	const value = useMemo<AuthContextValue>(
		() => ({
			...state,
			isLoggedIn: state.user !== null,
			isAdmin: state.user?.role === 'OWNER' || state.user?.role === 'ADMIN',
			isSuperAdmin: state.user?.role === 'SUPER_ADMIN',
			hasCompany: state.user?.companyId !== null && state.user?.companyId !== undefined,
			isImpersonating: state.impersonation !== null,
			setUser,
			init,
			login,
			signup,
			signupWithInvite,
			logout,
			startImpersonation,
			stopImpersonation
		}),
		[state, setUser, init, login, signup, signupWithInvite, logout, startImpersonation, stopImpersonation]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) throw new Error('useAuth must be used inside AuthProvider');
	return context;
}
